import { useState, useEffect } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { H2 } from 'foundations-components/transitional/components/H2';
import * as RequestStatus from 'ContentUtils/constants/RequestStatus';
import * as formsSelectors from 'ContentData/selectors/forms';
import * as FormsActions from 'ContentData/actions/Forms';
import { isSimpleWorkflowForForm } from 'ContentData/helpers/workflowsHelpers';
import { RemoveIcon, DeleteIcon } from 'foundations-assets';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.cmv2.fields.form.formWorkflowDependencies.${key}`;
function mapStateToProps(state, props) {
  const {
    formWorkflowDependency,
    formGuid
  } = props;
  const {
    flowId
  } = formWorkflowDependency || {};
  return {
    dependencyDeleteRequestStatus: formsSelectors.getFormWorkflowDependencyDeleteRequestStatus(state, {
      flowId,
      formGuid
    })
  };
}
const mapDispatchToProps = {
  deleteFormWorkflowDependency: FormsActions.deleteFormWorkflowDependency,
  deleteFormWorkflowDependencyAndWorkflow: FormsActions.deleteFormWorkflowDependencyAndWorkflow
};
const connector = connect(mapStateToProps, mapDispatchToProps);
const _RemovalModal = ({
  contentId,
  deleteFormWorkflowDependency,
  deleteFormWorkflowDependencyAndWorkflow,
  dependencyDeleteRequestStatus,
  formGuid,
  formWorkflowDependency,
  onClose,
  onRemoveWorkflowEvent,
  workflowLink
}) => {
  const [isRemoving, setIsRemoving] = useState(false);
  const prevDependencyDeleteRequestStatus = usePrevious(dependencyDeleteRequestStatus);
  const {
    canDelete,
    flowId,
    name
  } = formWorkflowDependency;
  const modalI18nKey = canDelete ? 'deleteWorkflowModal' : 'removeFormFromWorkflowModal';
  useEffect(() => {
    if (isRemoving && dependencyDeleteRequestStatus !== prevDependencyDeleteRequestStatus) {
      if (dependencyDeleteRequestStatus === RequestStatus.FAILED) {
        let deleteErrorI18nKey = 'removeFormFromWorkflowModal.removeError';
        if (canDelete) {
          deleteErrorI18nKey = 'deleteWorkflowModal.deleteError';
        }
        FloatingAlertStore.addAlert({
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey(deleteErrorI18nKey),
            options: {
              workflowName: name
            }
          }),
          type: 'danger'
        });
      }
      if (dependencyDeleteRequestStatus === RequestStatus.FAILED || dependencyDeleteRequestStatus === RequestStatus.SUCCEEDED) {
        setIsRemoving(false);
        onClose();
      }
    }
  }, [dependencyDeleteRequestStatus, prevDependencyDeleteRequestStatus, isRemoving, name, canDelete, onClose]);
  const onRemoveWorkflow = () => {
    setIsRemoving(true);
    if (onRemoveWorkflowEvent) onRemoveWorkflowEvent();
    if (canDelete) {
      deleteFormWorkflowDependencyAndWorkflow(flowId, formGuid).catch(err => {
        setTimeout(() => {
          throw err;
        });
      });
    } else {
      deleteFormWorkflowDependency(flowId, formGuid, contentId).catch(err => {
        setTimeout(() => {
          throw err;
        });
      });
    }
  };
  return /*#__PURE__*/_jsxs(Modal, {
    use: "danger",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`${modalI18nKey}.header`)
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: i18nKey(`${modalI18nKey}.message`),
          options: {
            workflowLink
          }
        })
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        loading: isRemoving,
        onClick: onRemoveWorkflow,
        use: "danger",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`${modalI18nKey}.confirm`)
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        use: "tertiary-light",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`${modalI18nKey}.cancel`)
        })
      })]
    })]
  });
};
export const RemovalModal = connector(_RemovalModal);
export const HasReferencesModal = ({
  onClose,
  workflowLink,
  workflowName
}) => {
  return /*#__PURE__*/_jsxs(Modal, {
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('hasReferencesModal.header')
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: i18nKey('hasReferencesModal.message'),
          options: {
            workflowName,
            workflowLink
          }
        })
      })
    }), /*#__PURE__*/_jsx(DialogFooter, {
      children: /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('hasReferencesModal.close')
        })
      })
    })]
  });
};
export const RemoveFormWorkflowDependencyButton = ({
  formGuid,
  formWorkflowDependency,
  hasWorkflowsDeleteAccess,
  onClick
}) => {
  const {
    canDelete,
    canRemoveEnrollmentCriteria
  } = formWorkflowDependency;
  let removeButtonTooltipI18nKey = 'dependencyRow.removeTooltip';
  let removeButtonIcon = /*#__PURE__*/_jsx(RemoveIcon, {});
  let removeIsDisabled = false;
  if (canDelete) {
    removeButtonTooltipI18nKey = 'dependencyRow.deleteTooltip';
    removeButtonIcon = /*#__PURE__*/_jsx(DeleteIcon, {});
    if (!hasWorkflowsDeleteAccess) {
      removeButtonTooltipI18nKey = 'dependencyRow.disabledDeleteTooltip';
      removeIsDisabled = true;
    }
  } else if (isSimpleWorkflowForForm(formWorkflowDependency, formGuid)) {
    removeButtonTooltipI18nKey = 'dependencyRow.simpleWorkflowRemoveTooltip';
    removeIsDisabled = true;
  } else if (!canRemoveEnrollmentCriteria) {
    removeButtonTooltipI18nKey = 'dependencyRow.disabledRemoveTooltip';
    removeIsDisabled = true;
  }
  return /*#__PURE__*/_jsx(IconButton, {
    "aria-label": "Remove workflow",
    disabled: removeIsDisabled,
    onClick: onClick,
    size: "xs",
    tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey(removeButtonTooltipI18nKey)
    }),
    children: removeButtonIcon
  });
};